import { useEffect, useRef, useState } from 'react'

import type { FunnelStage, FunnelStageKey } from '@/api/types'
import { changeTone, formatChange, percentChange } from '@/design'

import { isMeasured, measuredBefore, NO_FIGURE, NOT_MEASURED, stageButtonId, stepComparable } from './figures'
import { StagesTable } from './StagesTable'

const UNMEASURED = <span className="ci-funnel__unmeasured">{NOT_MEASURED}</span>

/** Sessions against the previous period's, coloured as good or bad news; "—" with nothing to compare. */
function SessionsChange({ stage }: { stage: FunnelStage }) {
  const change =
    isMeasured(stage) && stage.previous && measuredBefore([stage])
      ? percentChange(stage.sessions, stage.previous.sessions)
      : null
  if (change === null) return <>{NO_FIGURE}</>
  return (
    <span className="fun-change" data-tone={changeTone(change, 'up_good')}>
      {formatChange(change)}
    </span>
  )
}

/**
 * The seven stages with every figure the API gives for each: users, sessions,
 * conversion from the stage before and from the first, and the drop-off on the
 * way in. Each stage name opens (or closes) that stage's detail. A stage with
 * no events reads "Not measured", and so does a step into or out of it, never 0 %.
 */
export function FunnelTable({
  stages,
  selected,
  onSelect,
}: {
  stages: readonly FunnelStage[]
  selected: FunnelStageKey | undefined
  onSelect: (stage: FunnelStageKey) => void
}) {
  // A link that arrives with `?stage=` (the Overview funnel's) brings that stage's row into view, once, on arrival.
  const [arrivedAt] = useState(selected)
  const arrivedRow = useRef<HTMLTableRowElement>(null)
  useEffect(() => {
    arrivedRow.current?.scrollIntoView?.({ block: 'nearest' })
  }, [])

  return (
    <StagesTable
      caption="Conversion funnel stages"
      stages={stages}
      row={(stage, above) => {
        const measured = isMeasured(stage)
        return {
          key: stage.key,
          measured,
          comparable: stepComparable(stage, above),
          users: stage.users,
          sessions: stage.sessions,
          stageConversionRate: stage.stageConversionRate,
          totalConversionRate: stage.totalConversionRate,
          dropOff: stage.dropOff,
          dropOffRate: stage.dropOffRate,
          share: measured ? stage.totalConversionRate : null,
          missing: UNMEASURED,
        }
      }}
      header={(stage) => (
        <button
          type="button"
          id={stageButtonId(stage.key)}
          className="fun-table__stage"
          aria-pressed={stage.key === selected}
          onClick={() => onSelect(stage.key)}
        >
          {stage.label}
        </button>
      )}
      extra={{ label: 'Sessions vs previous', cell: (stage) => <SessionsChange stage={stage} /> }}
      rowRef={(stage) => (stage.key === arrivedAt ? arrivedRow : undefined)}
      selected={(stage) => stage.key === selected}
    />
  )
}
